/**
 * DateFormattingService
 * 
 * Handles all date formatting and calculation logic for control cards.
 */

/**
 * Service for date formatting and calculations
 */
export class DateFormattingService {
  /**
   * Format date to Norwegian format (DD.MM.YYYY)
   */
  formatNorwegianDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      
      return `${day}.${month}.${year}`
    } catch (error) {
      console.warn('Failed to format Norwegian date:', dateString, error)
      return 'N/A'
    }
  }

  /**
   * Calculate next inspection date based on performed date
   * All equipment gets 12 months inspection interval
   * 
   * @param performedDate - Date when inspection was performed
   * @param _category - Category code (reserved for future use if different categories need different intervals)
   * @returns Formatted next inspection date (MM-YYYY)
   */
  calculateNextInspectionDate(
    performedDate: string | null | undefined,
    _category?: string
  ): string {
    if (!performedDate) return 'N/A'
    
    try {
      const date = new Date(performedDate)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      
      // Add 12 months (1 year)
      date.setMonth(date.getMonth() + 12)
      
      // Format as MM-YYYY for next examination column
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      
      return `${month}-${year}`
    } catch (error) {
      console.warn('Failed to calculate next inspection date:', performedDate, error)
      return 'N/A'
    }
  }

  /**
   * Format date to month-year format (MM-YYYY)
   * Used for "Valid Until" / "Next Examination" display
   */
  formatMonthYear(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A'
    
    try {
      const date = new Date(dateString)
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A'
      }
      
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      
      return `${month}-${year}`
    } catch (error) {
      console.warn('Failed to format month-year date:', dateString, error)
      return 'N/A'
    }
  }

  /**
   * Check if equipment should show NDT dates based on category
   * NDT is required for: Chain slings (R1), Wire slings (R3), and F categories (lifting equipment)
   * NDT is NOT required for: Shackles (R4), hooks, and other hardware
   */
  shouldShowNDTDate(categoryCode: string | null | undefined): boolean {
    if (!categoryCode) return false
    
    const isNDTEligible = 
      categoryCode === 'R1' || 
      categoryCode === 'R3' || 
      categoryCode.startsWith('F')
    
    return isNDTEligible
  }

  /**
   * Format date value intelligently
   * Only formats if it looks like a date string
   */
  formatDateValue(value: string | null | undefined): string {
    if (!value) return value || ''
    
    // Only format if it looks like a date string (not just numbers)
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/,           // YYYY-MM-DD format
      /^\d{2}\.\d{2}\.\d{4}$/,         // DD.MM.YYYY format
      /^\d{2}\/\d{2}\/\d{4}$/,         // DD/MM/YYYY format
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,     // D/M/YYYY format
      /^\d{4}-\d{2}-\d{2}T/,           // ISO datetime format
      /^\d{4}-\d{2}-\d{2}\s/,          // YYYY-MM-DD with space
    ]
    
    const looksLikeDate = datePatterns.some(pattern => pattern.test(value))
    
    if (looksLikeDate) {
      return this.formatNorwegianDate(value)
    }
    
    return value
  }

  /**
   * Get current date in Norwegian format
   */
  getCurrentDateNorwegian(): string {
    return this.formatNorwegianDate(new Date().toISOString())
  }

  /**
   * Parse Norwegian date to ISO format
   */
  parseNorwegianDate(norwegianDate: string): string | null {
    // Expect DD.MM.YYYY format
    const match = norwegianDate.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
    
    if (!match) return null
    
    const [, day, month, year] = match
    return `${year}-${month}-${day}`
  }

  /**
   * Check if a date string is valid
   */
  isValidDate(dateString: string): boolean {
    try {
      const date = new Date(dateString)
      return !isNaN(date.getTime())
    } catch {
      return false
    }
  }

  /**
   * Add months to a date
   */
  addMonths(dateString: string, months: number): string | null {
    try {
      const date = new Date(dateString)
      
      if (isNaN(date.getTime())) {
        return null
      }
      
      date.setMonth(date.getMonth() + months)
      return date.toISOString()
    } catch {
      return null
    }
  }
}
